import userManager, { isLoggedOutLanding } from "./oidc";

interface EsriTokenResponse {
  token: string;
  expires: number;
  ssl: boolean;
}

interface KeycloakTokenResponse {
  access_token: string;
  expires_in: number;
  refresh_token: string;
  token_type: string;
  id_token: string;
  not_before_policy: number;
  session_state: string;
  scope: string;
}

class TokenService {
  static readonly TOKEN_UPDATED_EVENT = "mapet-token-updated";

  private static instance: TokenService;
  private token: string | null = null;
  private tokenExpiry: number | null = null;
  private tokenPromise: Promise<string | null> | null = null;

  private constructor() {
    this.syncTokenState();

    window.addEventListener("oidc-auth-logout", () => {
      console.log("OIDC logout event received, clearing Esri tokens");
      this.clearToken();
    });
  }

  public static getInstance(): TokenService {
    if (!TokenService.instance) {
      TokenService.instance = new TokenService();
    }
    return TokenService.instance;
  }

  /** Treat stored esriTokenExpiry as absolute unix time in milliseconds. */
  private parseStoredExpiry(raw: string): number | null {
    const expiry = Number(raw);
    if (!Number.isFinite(expiry) || expiry <= 0) {
      return null;
    }
    return expiry;
  }

  /** Esri token responses return `expires` as absolute unix time in milliseconds. */
  private parseEsriExpires(expires: number): number {
    if (expires > 1_000_000_000_000) {
      return expires;
    }
    if (expires > 1_000_000_000) {
      return expires * 1000;
    }
    return Date.now() + expires * 1000;
  }

  private isStoredTokenActive(expiryMs: number): boolean {
    const now = Date.now();
    const remainingMs = expiryMs - now;

    // Expired or within refresh buffer
    if (remainingMs <= TokenService.EXPIRY_BUFFER_MS) {
      return false;
    }

    // Legacy mis-parsed values stored far in the future
    if (remainingMs > TokenService.MAX_TOKEN_LIFETIME_MS) {
      return false;
    }

    return true;
  }

  private loadTokenFromStorage(): void {
    try {
      const storedToken = localStorage.getItem("esriToken");
      const storedExpiry = localStorage.getItem("esriTokenExpiry");

      if (!storedToken || !storedExpiry) {
        if (this.token !== null || storedToken || storedExpiry) {
          this.clearToken();
        }
        return;
      }

      const expiry = this.parseStoredExpiry(storedExpiry);
      if (expiry === null || !this.isStoredTokenActive(expiry)) {
        console.log(
          "[tokenService] Clearing inactive token. Expiry (ms):",
          expiry,
          expiry !== null ? new Date(expiry).toISOString() : "invalid",
          "Now:",
          new Date().toISOString(),
        );
        this.clearToken();
        return;
      }

      this.token = storedToken;
      this.tokenExpiry = expiry;
    } catch (error) {
      console.error("Error loading token from storage:", error);
      this.clearToken();
    }
  }

  private notifyTokenUpdated(): void {
    window.dispatchEvent(new Event(TokenService.TOKEN_UPDATED_EVENT));
  }

  /** Treat token as stale this many ms before actual expiry. */
  private static readonly EXPIRY_BUFFER_MS = 30_000;

  /** Max stored lifetime — guards against legacy mis-parsed expiry values. */
  private static readonly MAX_TOKEN_LIFETIME_MS = 24 * 60 * 60 * 1000;

  private isExpired(
    expiryMs: number,
    bufferMs = TokenService.EXPIRY_BUFFER_MS,
  ): boolean {
    return expiryMs <= Date.now() + bufferMs;
  }

  private normalizeEsriExpiry(expires: number): number {
    return this.parseEsriExpires(expires);
  }

  private isReasonableExpiry(expiryMs: number): boolean {
    return this.isStoredTokenActive(expiryMs);
  }

  /** Drop cached token when missing, expired, or out of sync with localStorage. */
  private syncTokenState(): void {
    this.loadTokenFromStorage();
  }

  private saveTokenToStorage(token: string, expiryMs: number): void {
    try {
      const tokenChanged = this.token !== token;
      const expiryChanged = this.tokenExpiry !== expiryMs;
      localStorage.setItem("esriToken", token);
      localStorage.setItem("esriTokenExpiry", expiryMs.toString());
      this.token = token;
      this.tokenExpiry = expiryMs;
      if (tokenChanged || expiryChanged) {
        this.notifyTokenUpdated();
      }
    } catch (error) {
      console.error("Error saving token to storage:", error);
    }
  }

  private saveTokenWithDuration(token: string, expiresInSeconds: number): void {
    this.saveTokenToStorage(token, Date.now() + expiresInSeconds * 1000);
  }

  private clearToken(): void {
    const hadStoredToken =
      this.token !== null || localStorage.getItem("esriToken") !== null;
    this.token = null;
    this.tokenExpiry = null;
    localStorage.removeItem("esriToken");
    localStorage.removeItem("esriTokenExpiry");
    if (hadStoredToken) {
      this.notifyTokenUpdated();
    }
  }

  /** Milliseconds until proactive refresh. null if no token or already stale. */
  public getMsUntilRefresh(
    bufferMs = TokenService.EXPIRY_BUFFER_MS,
  ): number | null {
    this.syncTokenState();
    if (
      this.tokenExpiry === null ||
      this.isExpired(this.tokenExpiry, bufferMs)
    ) {
      return null;
    }
    return Math.max(0, this.tokenExpiry - bufferMs - Date.now());
  }

  /** @deprecated Use getMsUntilRefresh */
  public getMsUntilExpiry(
    bufferMs = TokenService.EXPIRY_BUFFER_MS,
  ): number | null {
    return this.getMsUntilRefresh(bufferMs);
  }

  public isTokenValid(): boolean {
    this.syncTokenState();
    return this.token !== null && this.tokenExpiry !== null;
  }

  public getToken(): string | null {
    this.syncTokenState();
    return this.token;
  }

  public async getValidToken(
    tokenServiceUrl: string,
    client: string,
    options?: { force?: boolean },
  ): Promise<string | null> {
    this.syncTokenState();

    // After logout (or with no Keycloak session) never call MapET — that error
    // was racing logout and leaving the UI stuck on a blank /FWC page.
    if (isLoggedOutLanding()) {
      this.clearToken();
      return null;
    }

    if (!options?.force && this.token !== null) {
      return this.token;
    }

    if (options?.force) {
      this.clearToken();
    }

    // Get a fresh Keycloak token from userManager
    const user = await userManager.getUser();
    if (!user || !user.access_token) {
      this.clearToken();
      return null;
    }
    const keycloakToken = user.access_token;

    // If there's already a token request in progress, wait for it
    if (this.tokenPromise) {
      return this.tokenPromise;
    }

    // Start new token request
    this.tokenPromise = this.requestNewToken(
      keycloakToken,
      tokenServiceUrl,
      client,
    );

    try {
      const token = await this.tokenPromise;
      return token;
    } finally {
      this.tokenPromise = null;
    }
  }

  private async requestNewToken(
    keycloakToken: string,
    tokenServiceUrl: string,
    client: string,
  ): Promise<string | null> {
    try {
      // Check if we're calling the wrong endpoint
      if (tokenServiceUrl.includes("openid-connect")) {
        console.error(
          "ERROR: Token service URL contains openid-connect - this is wrong!",
        );
        console.error("Expected URL should contain MapET-SFS/rest/tokens");
        return null;
      }

      if (!tokenServiceUrl.includes("MapET-SFS")) {
        console.error("ERROR: Token service URL does not contain MapET-SFS");
        console.error("Expected URL should contain MapET-SFS/rest/tokens");
        return null;
      }

      console.log(
        "[tokenService] Requesting MapET token from:",
        tokenServiceUrl,
      );

      const response = await fetch(tokenServiceUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams({
          f: "json",
          client: client,
          keycloaktoken: keycloakToken,
          request: "getToken",
          expiration: "60",
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Token service error response:", errorText);
        throw new Error(
          `Token service request failed: ${response.status} ${response.statusText}`,
        );
      }

      const data = await response.json();

      // Check if this is an Esri token response
      if (data.token && typeof data.token === "string" && !data.access_token) {
        const esriResponse = data as EsriTokenResponse;
        const expiryMs = esriResponse.expires
          ? this.parseEsriExpires(esriResponse.expires)
          : Date.now() + 60 * 60 * 1000;
        console.log(
          "[tokenService] MapET token received. Expiry (ms):",
          expiryMs,
          new Date(expiryMs).toISOString(),
        );
        this.saveTokenToStorage(esriResponse.token, expiryMs);
        return esriResponse.token;
      } else if (data.access_token) {
        console.warn(
          "Received Keycloak response from Esri token service - this indicates a configuration issue",
        );

        const keycloakResponse = data as KeycloakTokenResponse;
        const expiresIn = keycloakResponse.expires_in || 300;
        this.saveTokenWithDuration(keycloakResponse.access_token, expiresIn);
        return keycloakResponse.access_token;
      } else {
        throw new Error("No valid token in response");
      }
    } catch (error) {
      console.error("Error requesting new token:", error);
      return null;
    }
  }

  public refreshToken(
    tokenServiceUrl: string,
    client: string,
  ): Promise<string | null> {
    return this.getValidToken(tokenServiceUrl, client, { force: true });
  }

  public clearStoredToken(): void {
    this.clearToken();
  }
}

export const MAPET_TOKEN_UPDATED_EVENT = TokenService.TOKEN_UPDATED_EVENT;

// Create and export the singleton instance
const tokenServiceInstance = TokenService.getInstance();
export default tokenServiceInstance;
